import React, { FC, useEffect, useState } from 'react'
import {
  Dropdown,
  MultiSelect,
  NumberInput,
  Checkbox
} from '@contrast-security-inc/design-system-inputs'
import { Box, Row } from '../../../../legacy-design-system/form'
import { css } from '@emotion/css'
import { JiraFormFieldTypes } from './jira-form-field-types'
import { useIntl } from 'react-intl'
import { MaintenanceRecurrence } from '../../../servers-grid/shared'
import { Label } from '../../../../legacy-design-system/form-label'
import {
  NgBugtrackerMaintenanceWindowRequest,
  NgBugtrackerMaintenanceWindowResource
} from '../api/shared'
import { useFormContext } from 'react-hook-form'
import { JiraIntegrationForm } from './jira-form-schema'

const SECONDS_IN_HOUR = 3600
const SECONDS_IN_MINUTE = 60

interface PeriodOption {
  type: MaintenanceRecurrence
  displayName: string
}

interface DurationOption {
  value: number
  displayName: string
}

interface Day {
  label: string
  value: number
}

const styles = {
  container: css`
    display: flex;
    gap: 16px;
    width: 100%;
  `,
  input: css`
    flex-grow: 1;
    min-width: 100px;
  `,
  row: css`
    margin-top: 10px;
  `,
  inputFlexStyle: css`
    flex-grow: 1;
    min-width: 150px;
  `
}

export interface MaintenanceWindowConfigProps {
  maintenanceWindow?: NgBugtrackerMaintenanceWindowResource
}

export const MaintenanceWindowConfig: FC<MaintenanceWindowConfigProps> = ({
  maintenanceWindow
}: MaintenanceWindowConfigProps) => {
  const { formatMessage } = useIntl()

  const periodOptions: PeriodOption[] = [
    { type: 'DAILY', displayName: formatMessage({ id: 'title_daily' }) },
    { type: 'WEEKLY', displayName: formatMessage({ id: 'title_weekly' }) },
    { type: 'MONTHLY', displayName: formatMessage({ id: 'title_monthly' }) }
  ]

  const dayOptions: Day[] = [
    { label: formatMessage({ id: 'title_monday' }), value: 1 },
    { label: formatMessage({ id: 'title_tuesday' }), value: 2 },
    { label: formatMessage({ id: 'title_wednesday' }), value: 3 },
    { label: formatMessage({ id: 'title_thursday' }), value: 4 },
    { label: formatMessage({ id: 'title_friday' }), value: 5 },
    { label: formatMessage({ id: 'title_saturday' }), value: 6 },
    { label: formatMessage({ id: 'title_sunday' }), value: 7 }
  ]

  // utils
  const getSelectedDays = (type: string, days: number[] = []) => {
    let selectedDays: Day[] = []

    if (type === 'WEEKLY') {
      selectedDays = dayOptions
    } else if (type === 'MONTHLY') {
      selectedDays = monthOptions
    }

    return selectedDays.filter((day) => days.includes(day.value))
  }

  const monthOptions: Day[] = Array.from({ length: 31 }, (_, i) => ({
    label: (i + 1).toString(),
    value: i + 1
  }))

  const convertSecondsToTime = (seconds: number) => {
    const hours = Math.floor(seconds / SECONDS_IN_HOUR)
    const remainingSeconds = seconds % SECONDS_IN_HOUR
    const minutes = Math.floor(remainingSeconds / SECONDS_IN_MINUTE)

    return { hours, minutes }
  }

  const getDurationOptions = (hours: number) => {
    const minutes = [
      { value: 15, displayName: '15' },
      { value: 30, displayName: '30' },
      { value: 45, displayName: '45' }
    ]

    if (hours > 0) {
      minutes.unshift({ value: 0, displayName: '0' })
    }

    return minutes
  }

  //  used to update the parent form
  const { setValue } = useFormContext<JiraIntegrationForm>()

  const [isMaintenanceWindowEnabled, setIsMaintenanceWindowEnabled] = useState(
    !!maintenanceWindow
  )

  const recurrence = periodOptions.find(
    (option) => option.type === maintenanceWindow?.recurrence
  )
  const [selectedPeriodType, setSelectedPeriodType] = useState<PeriodOption>(
    recurrence ?? periodOptions[0]
  )
  const [selectedDays, setSelectedDays] = useState<Day[]>(
    recurrence
      ? getSelectedDays(recurrence.type, maintenanceWindow?.recurrenceDays)
      : []
  )

  const { hours: defaultStartHour, minutes: defaultStartMinute } =
    convertSecondsToTime(maintenanceWindow?.startTimeSeconds ?? 0)
  const [startHour, setStartHour] = useState(defaultStartHour)
  const [startMinute, setStartMinute] = useState(defaultStartMinute)
  const [startHourError, setStartHourError] = useState('')
  const [startMinuteError, setStartMinuteError] = useState('')

  const { hours: defaultHours, minutes: defaultMinutes } = convertSecondsToTime(
    maintenanceWindow?.durationSeconds ?? 0
  )
  const [maintenanceDurationMinutes, setMaintenanceDurationMinutes] =
    useState<number>(defaultMinutes)
  const [maintenanceDurationHours, setMaintenanceDurationHours] =
    useState<number>(defaultHours)
  const [minuteOptions, setMinuteOptions] = useState<DurationOption[]>(
    getDurationOptions(maintenanceDurationHours)
  )

  // effects
  useEffect(() => {
    setMinuteOptions(getDurationOptions(maintenanceDurationHours))
  }, [maintenanceDurationHours])

  const resetForm = (period: PeriodOption) => {
    // clear parent form fields
    setValue(JiraFormFieldTypes.MAINTENANCE_WINDOW, undefined)

    setSelectedPeriodType(period)
    setStartHour(12)
    setStartMinute(0)
    setStartHourError('')
    setStartMinuteError('')
    setMaintenanceDurationHours(0)
    setSelectedDays([])
  }

  const handleStartHourChange = (
    _event: React.ChangeEvent<HTMLInputElement>,
    value: string
  ): void => {
    const numValue = Number(value)

    if (isNaN(numValue) || numValue < 0 || numValue > 23) {
      setStartHourError(
        formatMessage({ id: 'title_integrationsSettings_invalidHour' })
      )
    } else {
      setStartHourError('')
      setStartHour(numValue)
    }
  }

  const handleStartMinuteChange = (
    _event: React.ChangeEvent<HTMLInputElement>,
    value: string
  ) => {
    const numValue = Number(value)

    if (isNaN(numValue) || numValue < 0 || numValue > 59) {
      setStartMinuteError(
        formatMessage({ id: 'title_integrationsSettings_invalidMinute' })
      )
    } else {
      setStartMinuteError('')
      setStartMinute(numValue)
    }
  }

  const calculateTimeInSeconds = (hours: number, minutes: number): number => {
    return hours * SECONDS_IN_HOUR + minutes * SECONDS_IN_MINUTE
  }

  const getRecurrenceDays = (): number[] => {
    if (selectedDays.length > 0) {
      return selectedDays.map((day) => day.value)
    }

    switch (selectedPeriodType.type) {
      case 'WEEKLY':
        return dayOptions.map((day) => day.value)
      case 'MONTHLY':
        return monthOptions.map((day) => day.value)
      default:
        return []
    }
  }

  useEffect(() => {
    if (isMaintenanceWindowEnabled) {
      const maintenanceWindow: NgBugtrackerMaintenanceWindowRequest = {
        recurrence: selectedPeriodType.type,
        recurrence_days: getRecurrenceDays(),
        start_time_seconds: calculateTimeInSeconds(startHour, startMinute),
        duration_seconds: calculateTimeInSeconds(
          maintenanceDurationHours,
          maintenanceDurationMinutes
        )
      }

      setValue(JiraFormFieldTypes.MAINTENANCE_WINDOW, maintenanceWindow)
    }
  }, [
    isMaintenanceWindowEnabled,
    selectedPeriodType,
    startHour,
    startMinute,
    maintenanceDurationHours,
    maintenanceDurationMinutes,
    selectedDays
  ])

  return (
    <Row>
      <Box basis={12}>
        <div className={styles.container}>
          <Checkbox
            testId="isMaintenanceWindow-checkbox"
            label={formatMessage({
              id: 'title_integrationsSettings_enableMaintenanceWindow'
            })}
            isChecked={isMaintenanceWindowEnabled}
            onChange={(_, value) => {
              setIsMaintenanceWindowEnabled(!!value)

              if (!value) {
                resetForm(periodOptions[0])
              }
            }}
          />
        </div>

        {isMaintenanceWindowEnabled ? (
          <Box basis={12}>
            <Row>
              <div className={styles.container}>
                <Dropdown<PeriodOption>
                  testId="periodType-dropdown"
                  label={formatMessage({
                    id: 'title_integrationsSettings_periodType'
                  })}
                  items={periodOptions}
                  itemToString={(item) => item.displayName}
                  isRequired
                  onChange={(_event, selectedPeriod) => {
                    if (selectedPeriod) {
                      resetForm(selectedPeriod)
                    }
                  }}
                  value={selectedPeriodType}
                />

                {selectedPeriodType?.type === 'WEEKLY' && (
                  <MultiSelect
                    testId="weekDays-multiSelect"
                    label={formatMessage({
                      id: 'title_integrationsSettings_daysOfTheWeek'
                    })}
                    placeholderTag={formatMessage(
                      { id: 'detail_integrationsSettings_allDaysPlaceholder' },
                      { amountDays: dayOptions.length }
                    )}
                    items={dayOptions}
                    itemToString={(item) => (item ? item.label : '')}
                    itemToID={(item) => item.value.toString()}
                    onChange={(_, value) => {
                      setSelectedDays(value || [])
                    }}
                    value={selectedDays}
                  />
                )}

                {selectedPeriodType?.type === 'MONTHLY' && (
                  <MultiSelect
                    testId="monthDays-multiSelect"
                    label={formatMessage({
                      id: 'title_integrationsSettings_daysOfTheMonth'
                    })}
                    placeholderTag={formatMessage(
                      { id: 'detail_integrationsSettings_allDaysPlaceholder' },
                      { amountDays: monthOptions.length }
                    )}
                    items={monthOptions}
                    itemToString={(item) => (item ? item.label : '')}
                    itemToID={(item) => item.value.toString()}
                    onChange={(_, value) => {
                      setSelectedDays(value || [])
                    }}
                    value={selectedDays}
                  />
                )}
              </div>
            </Row>

            <Row className={styles.row}>
              <Label>
                {formatMessage({
                  id: 'title_integrationsSettings_startTimeOfTheMaintenance'
                })}
              </Label>
              <Box basis={12}>
                <Row>
                  <div className={styles.container}>
                    <NumberInput
                      testId="startHours-input"
                      label={formatMessage({
                        id: 'title_integrationsSettings_hours'
                      })}
                      min={'0'}
                      max={'23'}
                      value={startHour.toString()}
                      onChange={handleStartHourChange}
                      className={styles.input}
                      error={startHourError}
                    />

                    <NumberInput
                      testId="startMinutes-input"
                      label={formatMessage({
                        id: 'title_integrationsSettings_minutes'
                      })}
                      min={'0'}
                      max={'59'}
                      value={startMinute.toString()}
                      onChange={handleStartMinuteChange}
                      className={styles.input}
                      error={startMinuteError}
                    />
                  </div>
                </Row>
              </Box>
            </Row>

            <Row className={styles.row}>
              <Label>
                {formatMessage({
                  id: 'title_integrationsSettings_durationOfTheMaintenance'
                })}
              </Label>
              <Box basis={12}>
                <Row>
                  <div className={styles.container}>
                    <NumberInput
                      testId="durationHours-input"
                      label={formatMessage({
                        id: 'title_integrationsSettings_maintenanceDurationHours'
                      })}
                      min={'0'}
                      max={'24'}
                      value={maintenanceDurationHours.toString()}
                      onChange={(_event, value) => {
                        setMaintenanceDurationHours(Number(value))
                      }}
                      className={styles.inputFlexStyle}
                    />
                    <Dropdown<DurationOption>
                      testId="durationMinutes-dropdown"
                      label={formatMessage({
                        id: 'title_integrationsSettings_maintenanceDurationMinutes'
                      })}
                      items={minuteOptions}
                      itemToString={(item) => item.displayName}
                      isRequired
                      onChange={(_event, selectedDuration) => {
                        if (selectedDuration) {
                          setMaintenanceDurationMinutes(selectedDuration.value)
                        }
                      }}
                      value={
                        minuteOptions.find(
                          (option) =>
                            option.value === maintenanceDurationMinutes
                        ) || minuteOptions[0]
                      }
                    />
                  </div>
                </Row>
              </Box>
            </Row>
          </Box>
        ) : null}
      </Box>
    </Row>
  )
}
